const VALID_ROLE_CODES = new Set(['super_admin', 'admin', 'operator', 'viewer']);

const ROLE_ALIASES = {
  visualizador: 'viewer',
  operador: 'operator',
  administrador: 'admin',
  'super administrador': 'super_admin',
  superadmin: 'super_admin',
};

/** Normaliza lo que devuelve /auth/me/admin o formularios (código, etiqueta u objeto). */
export function normalizeAdminRoleCode(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    return normalizeAdminRoleCode(raw.code ?? raw.role_code ?? raw.role ?? raw.name);
  }
  const s = String(raw).trim().toLowerCase();
  if (ROLE_ALIASES[s]) return ROLE_ALIASES[s];
  if (VALID_ROLE_CODES.has(s)) return s;
  const byLabel = ADMIN_ROLES.find((r) => r.label.toLowerCase() === s);
  return byLabel ? byLabel.code : null;
}

/** Roles del panel de administración (tabla `roles`). */
export const ADMIN_ROLES = [
  {
    code: 'super_admin',
    label: 'Super Administrador',
    color: 'primary',
    description: 'Control total del sistema.',
    permissions: [
      'Gestionar usuarios administradores',
      'Configurar empresa, logo y redes',
      'Productos, inventario y categorías',
      'Compras, facturas y carritos',
      'Consultorios y actividad del sistema',
    ],
  },
  {
    code: 'admin',
    label: 'Administrador',
    color: 'info',
    description: 'Operación completa de la tienda, sin gestión de usuarios admin.',
    permissions: [
      'Productos, inventario y categorías',
      'Compras, facturas y carritos',
      'Configuración de contacto y empresa',
      'Consultorios y logs (lectura)',
      'No puede crear ni editar usuarios admin',
    ],
  },
  {
    code: 'operator',
    label: 'Operador',
    color: 'warning',
    description: 'Gestión operativa de pedidos y atención de ventas.',
    permissions: [
      'Ver y actualizar compras y carritos',
      'Ver facturas',
      'Panel y reportes en lectura parcial',
      'No modifica productos ni configuración',
      'No gestiona usuarios admin',
    ],
  },
  {
    code: 'viewer',
    label: 'Visualizador',
    color: 'secondary',
    description: 'Solo consulta: no puede guardar, crear ni eliminar.',
    permissions: [
      'Ver panel, productos, compras y facturas',
      'Ver carritos abandonados y logs',
      'Sin cambios en catálogo ni empresa',
      'Sin acceso a usuarios administradores',
    ],
  },
];

const ROUTE_RULES = {
  '/admin/usuarios-admin': (p) => p.canManageAdminUsers,
  '/admin/contacto': (p) => p.canManageCompany,
  '/admin/newsletter': (p) => p.canManageCompany,
  '/admin/consultorios': (p) => p.canManageConsultorios,
};

export function getAdminRoleMeta(code) {
  return ADMIN_ROLES.find((r) => r.code === code) || ADMIN_ROLES.find((r) => r.code === 'viewer');
}

export function getAdminPermissions(roleCode) {
  const code = normalizeAdminRoleCode(roleCode) || 'viewer';
  const isSuperAdmin = code === 'super_admin';
  const isViewer = code === 'viewer';
  const isAdmin = code === 'admin';
  const isOperator = code === 'operator';

  return {
    roleCode: code,
    roleLabel: getAdminRoleMeta(code).label,
    isSuperAdmin,
    isViewer,
    isAdmin,
    isOperator,
    canManageAdminUsers: isSuperAdmin,
    canManageCompany: isSuperAdmin || isAdmin,
    canManageConsultorios: isSuperAdmin || isAdmin,
    canManageProducts: isSuperAdmin || isAdmin,
    canManageOrders: isSuperAdmin || isAdmin || isOperator,
    canWrite: !isViewer,
    canAccessLogs: true,
  };
}

export function canAccessAdminRoute(roleCode, pathname) {
  const perms = getAdminPermissions(roleCode);
  const rule = ROUTE_RULES[pathname];
  if (rule) return rule(perms);
  return true;
}

export function getAssignableAdminRoles(currentRoleCode) {
  const perms = getAdminPermissions(currentRoleCode);
  return ADMIN_ROLES.filter((r) => {
    if (r.code === 'super_admin' && !perms.isSuperAdmin) return false;
    return true;
  });
}

export function filterAdminMenuByRole(menuItems, roleCode) {
  const perms = getAdminPermissions(roleCode);
  return menuItems.filter((item) => canAccessAdminRoute(roleCode, item.path));
}
